import { useState } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { Box, Typography, Chip, Divider, Paper } from '@mui/material';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import type { Employee } from '../../types';
import { formatDistanceToNow } from 'date-fns';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';
const MAP_ID = 'field-force-map';

interface EmployeeMapProps {
  employees: Employee[];
  onMarkerClick?: (employee: Employee) => void;
  defaultCenter?: { lat: number; lng: number };
  defaultZoom?: number;
  height?: number | string;
}

type EmployeeStatus = 'active' | 'idle' | 'interrupted' | 'offline';

function getEmployeeStatus(employee: Employee): EmployeeStatus {
  if (employee.lastLocation) {
    const lastUpdate = new Date(employee.lastLocation.timestamp);
    const minutesAgo = (Date.now() - lastUpdate.getTime()) / 60000;
    if (minutesAgo < 5) return 'active';
    if (minutesAgo < 30) return 'idle';
    return 'interrupted';
  }
  if (employee.onDuty) return 'active';
  return 'offline';
}

const STATUS_COLORS: Record<EmployeeStatus, string> = {
  active: '#059669',
  idle: '#D97706',
  interrupted: '#DC2626',
  offline: '#64748B',
};

const STATUS_LABELS: Record<EmployeeStatus, string> = {
  active: 'Active',
  idle: 'Idle (5-30 min)',
  interrupted: 'Interrupted (>30 min)',
  offline: 'Off Duty',
};

interface MarkerDotProps {
  color: string;
  label: string;
}

function MarkerDot({ color, label }: MarkerDotProps) {
  return (
    <Box
      title={label}
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: color,
        border: '3px solid white',
        boxShadow: `0 2px 8px ${color}80, 0 0 0 2px ${color}40`,
        cursor: 'pointer',
        transition: 'transform 0.15s',
        '&:hover': {
          transform: 'scale(1.3)',
        },
      }}
    />
  );
}

export function EmployeeMap({
  employees,
  onMarkerClick,
  defaultCenter = { lat: 20.5937, lng: 78.9629 },
  defaultZoom = 5,
  height = 500,
}: EmployeeMapProps) {
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const employeesWithLocation = employees.filter((e) => e.lastLocation);

  const handleMarkerClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    onMarkerClick?.(employee);
  };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <Box sx={{ height, width: '100%', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
        <Map
          mapId={MAP_ID}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          gestureHandling="greedy"
          disableDefaultUI={false}
          style={{ width: '100%', height: '100%' }}
        >
          {employeesWithLocation.map((employee) => {
            const status = getEmployeeStatus(employee);
            const color = STATUS_COLORS[status];
            return (
              <AdvancedMarker
                key={employee.id}
                position={{
                  lat: employee.lastLocation!.latitude,
                  lng: employee.lastLocation!.longitude,
                }}
                onClick={() => handleMarkerClick(employee)}
              >
                <MarkerDot color={color} label={`${employee.name} — ${STATUS_LABELS[status]}`} />
              </AdvancedMarker>
            );
          })}

          {selectedEmployee?.lastLocation && (
            <InfoWindow
              position={{
                lat: selectedEmployee.lastLocation.latitude,
                lng: selectedEmployee.lastLocation.longitude,
              }}
              onCloseClick={() => setSelectedEmployee(null)}
            >
              <Box sx={{ p: 0.5, minWidth: 200, maxWidth: 260 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonPinCircleIcon
                    sx={{ color: STATUS_COLORS[getEmployeeStatus(selectedEmployee)], fontSize: 20 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.875rem' }}>
                      {selectedEmployee.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedEmployee.employeeCode}
                    </Typography>
                  </Box>
                </Box>

                <Chip
                  label={STATUS_LABELS[getEmployeeStatus(selectedEmployee)]}
                  size="small"
                  sx={{
                    bgcolor: STATUS_COLORS[getEmployeeStatus(selectedEmployee)],
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    mb: 1,
                  }}
                />

                <Divider sx={{ my: 1 }} />

                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Last seen:{' '}
                      {formatDistanceToNow(new Date(selectedEmployee.lastLocation!.timestamp), {
                        addSuffix: true,
                      })}
                    </Typography>
                  </Box>

                  {selectedEmployee.lastLocation.batteryLevel !== undefined && (
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <BatteryChargingFullIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Battery: {selectedEmployee.lastLocation.batteryLevel}%
                      </Typography>
                    </Box>
                  )}

                  {selectedEmployee.todayVisitsCount !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                      Visits today: {selectedEmployee.todayVisitsCount}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    📍 {selectedEmployee.lastLocation.latitude.toFixed(4)},{' '}
                    {selectedEmployee.lastLocation.longitude.toFixed(4)}
                  </Typography>
                </Box>
              </Box>
            </InfoWindow>
          )}
        </Map>

        {/* Legend */}
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            bottom: 24,
            right: 12,
            p: 1.5,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            minWidth: 170,
          }}
        >
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.25 }}>
            EMPLOYEE STATUS
          </Typography>
          {(Object.entries(STATUS_COLORS) as [EmployeeStatus, string][]).map(([status, color]) => (
            <Box key={status} display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {STATUS_LABELS[status]}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Box>
    </APIProvider>
  );
}
